import type React from 'react';
import { Check, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { OrderStatusLabels } from '@/types/enums';
import type { OrderStatus } from '@/types/enums';

interface StatusTimelineProps {
  currentStatus: OrderStatus;
  handedOffAt?: string | null;
  createdAt: string;
}

const TIMELINE_STEPS: OrderStatus[] = [
  'ARRIVED',
  'PARTIALLY_HANDED_OFF',
  'COMPLETED',
];

const STATUS_ORDER: Record<string, number> = {};
TIMELINE_STEPS.forEach((s, i) => { STATUS_ORDER[s] = i; });

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const StatusTimeline: React.FC<StatusTimelineProps> = ({ currentStatus, handedOffAt, createdAt }) => {
  const currentIdx = STATUS_ORDER[currentStatus] ?? -1;

  return (
    <div className="space-y-1">
      {TIMELINE_STEPS.map((step, idx) => {
        const isPast = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isFuture = idx > currentIdx;

        let dateStr = '';
        if (step === 'ARRIVED') dateStr = formatDate(createdAt);
        if (step === 'PARTIALLY_HANDED_OFF') dateStr = formatDate(handedOffAt);
        if (step === 'COMPLETED') dateStr = formatDate(handedOffAt);

        return (
          <div key={step} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center border-2 shrink-0',
                  isPast && 'bg-green-500 border-green-500 text-white',
                  isCurrent && 'bg-blue-600 border-blue-600 text-white',
                  isFuture && 'bg-white border-gray-200 text-gray-300',
                )}
              >
                {isPast ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}
              </div>
              {idx < TIMELINE_STEPS.length - 1 && (
                <div
                  className={cn(
                    'w-0.5 h-6',
                    isPast ? 'bg-green-300' : 'bg-gray-200',
                  )}
                />
              )}
            </div>
            <div className={cn('pt-0.5', isFuture && 'opacity-40')}>
              <p className={cn('text-sm font-medium', isCurrent && 'text-blue-700')}>
                {OrderStatusLabels[step]}
              </p>
              {dateStr && <p className="text-xs text-gray-500">{dateStr}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusTimeline;
